import { useEffect, useRef } from 'react'

interface EngagementTimerOptions {
  /** Timer only counts while enabled (e.g. no other modal open). */
  enabled: boolean
  /** Fire after this many seconds of visible time on the page. */
  totalSeconds: number
  /** Optional shortcut: fire after this many seconds while `focused` is true. */
  focusedSeconds?: number
  focused?: boolean
  onReached: () => void
}

/**
 * Counts seconds the user actually spends looking at the page (tab visible,
 * timer enabled) and fires once when either threshold is reached.
 */
export function useEngagementTimer({
  enabled,
  totalSeconds,
  focusedSeconds,
  focused = false,
  onReached,
}: EngagementTimerOptions) {
  const totalRef = useRef(0)
  const focusedRef = useRef(0)
  const firedRef = useRef(false)
  const focusedFlagRef = useRef(focused)
  const onReachedRef = useRef(onReached)
  focusedFlagRef.current = focused
  onReachedRef.current = onReached

  useEffect(() => {
    if (!enabled || firedRef.current) return
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      totalRef.current += 1
      if (focusedFlagRef.current) focusedRef.current += 1
      const reachedTotal = totalRef.current >= totalSeconds
      const reachedFocused =
        focusedSeconds !== undefined && focusedRef.current >= focusedSeconds
      if (reachedTotal || reachedFocused) {
        firedRef.current = true
        window.clearInterval(interval)
        onReachedRef.current()
      }
    }, 1000)
    return () => window.clearInterval(interval)
  }, [enabled, totalSeconds, focusedSeconds])
}
